import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Tooltip.module.css";

type Placement = "top" | "bottom";
type Align = "center" | "right" | "left";

interface Props {
  content: ReactNode;
  children: ReactNode;
  // Slight delay so a quick mouse fly-past doesn't flash a
  // tooltip. 100ms matches the "snappy but not instant" feel.
  delayMs?: number;
  // PREFERRED side. The bubble goes to the other side when this one has no room.
  placement?: Placement;
  // PREFERRED horizontal anchor: "center" centres the bubble on the trigger, "right"
  // lines up its right edge (opens leftward), "left" its left edge. Either way it is
  // then kept inside the viewport.
  align?: Align;
}

const GAP = 6;
const EDGE = 8;
// The arrow stays this far inside the bubble's rounded corners.
const ARROW_INSET = 10;

interface Place {
  side: Placement;
  style: CSSProperties;
}

// At the viewport's corner while it is measured, so its width isn't squeezed.
const HIDDEN: Place = {
  side: "top",
  style: { visibility: "hidden", top: 0, left: 0 },
};

// In the body, fixed to the trigger (as InfoToggle): a sticky table header painted over
// a bubble left inside its row, and a table's scroll box clipped one near its edge.
export const Tooltip = ({
  content,
  children,
  delayMs = 100,
  placement = "top",
  align = "center",
}: Props) => {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Place>(HIDDEN);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const bubbleRef = useRef<HTMLSpanElement | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  // Measured before paint, so the bubble never shows in the wrong place.
  useLayoutEffect(() => {
    const anchor = wrapRef.current?.getBoundingClientRect();
    const box = bubbleRef.current;
    if (!open || !anchor || !box) return;
    const width = box.offsetWidth;
    const height = box.offsetHeight;
    const fitsAbove = anchor.top - GAP - height >= EDGE;
    const fitsBelow = anchor.bottom + GAP + height <= window.innerHeight - EDGE;
    const side: Placement =
      placement === "top"
        ? fitsAbove || !fitsBelow
          ? "top"
          : "bottom"
        : fitsBelow || !fitsAbove
          ? "bottom"
          : "top";
    const preferred =
      align === "right"
        ? anchor.right - width
        : align === "left"
          ? anchor.left
          : anchor.left + anchor.width / 2 - width / 2;
    const left = Math.max(
      EDGE,
      Math.min(preferred, window.innerWidth - width - EDGE),
    );
    const arrow = Math.max(
      ARROW_INSET,
      Math.min(anchor.left + anchor.width / 2 - left, width - ARROW_INSET),
    );
    setPlace({
      side,
      style: {
        top: side === "top" ? anchor.top - GAP - height : anchor.bottom + GAP,
        left,
        "--arrow-x": `${arrow}px`,
      } as CSSProperties,
    });
  }, [open, placement, align]);

  const close = useCallback(() => {
    setOpen(false);
    setPlace(HIDDEN);
  }, []);

  // A fixed bubble would be left behind by a scroll, so it closes instead.
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, close]);

  const show = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setOpen(true), delayMs);
  };
  const hide = () => {
    if (timer.current) clearTimeout(timer.current);
    close();
  };

  return (
    <span
      ref={wrapRef}
      className={styles.wrap}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open &&
        createPortal(
          <span
            ref={bubbleRef}
            role="tooltip"
            className={`${styles.bubble} ${place.side === "bottom" ? styles.bubbleBottom : ""}`}
            style={place.style}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
};
